/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import { css, cx } from "@panda/css";
import { Flex } from "@panda/jsx";

import { LogoList } from "./LogoList.molecule";
import { LogoListSectionProps } from "@components/elements/ref-sections";

const defaultLogoHeight = 54;
const gridLogoHeight = 90;

/**
 * Renders a list of logos either with an animation or in a grid.
 * When the marquee animation is enabled a second set of logos is rendered to
 *   appear like they are looping around the screen.
 */
export const LogoMarquee = ({
	logos = [],
	disableMarquee = false,
	className,
}: LogoListSectionProps): React.JSX.Element | null => {
	const [grid, setGrid] = useState<boolean>(false);

	//Set high to ensure the first render doesn't scale the images down
	// and we can get an accurate getBoundingClientRect()
	const [logoListWidth, setLogoListWidth] = useState(2400);

	const logoListRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

	// gets the current width of all the logos and compares it to the client width
	const checkWidth = useCallback(() => {
		if (logoListRef.current) {
			const width = logoListRef.current.getBoundingClientRect().width;
			setLogoListWidth(width);

			//compare with a small buffer for the scrollbar
			setGrid(width + 20 < document.body.clientWidth);
		}
	}, [logoListRef, setGrid, setLogoListWidth]);

	// client side DOM width check to determine if the animation should run
	useEffect(() => {
		if (!disableMarquee) {
			checkWidth();

			window.addEventListener("resize", checkWidth);
			return () => window.removeEventListener("resize", checkWidth);
		}
		return () => {};
	}, [disableMarquee, checkWidth]);

	const marquee = !disableMarquee && !grid;

	if (!logos) {
		return null;
	}

	const height = disableMarquee ? gridLogoHeight : defaultLogoHeight;

	return (
		<div
			className={cx(
				"logo-marquee",
				css({
					height: `${height}px`,
					overflow: marquee ? "hidden" : "initial",
					w: "full",
				}),
				className,
			)}
		>
			<Flex
				className={css({ position: "relative", overflowX: "clip" })}
				style={{ minWidth: marquee ? `${logoListWidth}px` : "" }}
				justifyContent="center"
			>
				{marquee ? <LogoList aria-hidden={true} logos={logos} height={defaultLogoHeight} marquee delay /> : ""}
				<LogoList ref={logoListRef} logos={logos} height={height} marquee={marquee} gridLayout={disableMarquee} />
			</Flex>
		</div>
	);
};
